import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Snackbar,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { merchantsService, dictionariesService } from '../../../services';
import { Merchant, Currency, MerchantBalance } from '../../../types/index';
import AnimatedPage from '../../../components/AnimatedPage';
import { useMerchantBalances } from '../hooks/useMerchantBalances';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { canPerformAction, ActionKey } from '../../../auth/actionMatrix';
import { useNotification } from '../../../contexts/NotificationContext';

interface MerchantBalanceFormData {
  merchant_id: string;
  currency_id: string;
  balance: string;
  blocked_balance_in: string;
  blocked_balance_out: string;
}

interface MerchantBalancesRef {
  handleOpenDialog: () => void;
}

const MerchantBalances = forwardRef<MerchantBalancesRef>((props, ref) => {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState<MerchantBalanceFormData>({
    merchant_id: '',
    currency_id: '',
    balance: '0.00',
    blocked_balance_in: '0.00',
    blocked_balance_out: '0.00',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBalanceId, setSelectedBalanceId] = useState<string | null>(null);
  const [selectedBalance, setSelectedBalance] = useState<MerchantBalance | null>(null);
  const [search, setSearch] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const { balances, loading, error, refetch } = useMerchantBalances();
  const filteredBalances = useMemo(() => { const needle = search.trim().toLowerCase(); return balances.filter((balance) => (!needle || balance.merchant_name?.toLowerCase().includes(needle) || balance.merchant_id.toLowerCase().includes(needle)) && (!currencyFilter || balance.currency_id === currencyFilter)); }, [balances, search, currencyFilter]);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { showNotification } = useNotification();

  const canCreate = useMemo(
    () => canPerformAction(currentUser, ActionKey.MERCHANT_BALANCES_CREATE),
    [currentUser],
  );
  const canUpdate = useMemo(
    () => canPerformAction(currentUser, ActionKey.MERCHANT_BALANCES_UPDATE),
    [currentUser],
  );
  const canDelete = useMemo(
    () => canPerformAction(currentUser, ActionKey.MERCHANT_BALANCES_DELETE),
    [currentUser],
  );

  useImperativeHandle(ref, () => ({
    handleOpenDialog: () => {
      if (!canCreate) {
        showNotification('У вас нет прав для создания балансов мерчантов', 'error');
        return;
      }
      handleOpenDialog();
    }
  }));

  const fetchMerchants = async () => {
    try {
      const merchantsData = await merchantsService.getMerchants();
      setMerchants(merchantsData);
    } catch (err: any) {
      console.error('Error fetching merchants:', err);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const currenciesData = await dictionariesService.getCurrencies();
      setCurrencies(currenciesData);
    } catch (err: any) {
      console.error('Error fetching currencies:', err);
    }
  };

  useEffect(() => {
    fetchMerchants();
    fetchCurrencies();
  }, []);

  const handleOpenDialog = (balance?: MerchantBalance) => {
    if (balance) {
      setSelectedBalance(balance);
      setFormData({
        merchant_id: balance.merchant_id,
        currency_id: balance.currency_id,
        balance: balance.balance,
        blocked_balance_in: balance.blocked_balance_in,
        blocked_balance_out: balance.blocked_balance_out,
      });
    } else {
      setSelectedBalance(null);
      setFormData({
        merchant_id: '',
        currency_id: '',
        balance: '0.00',
        blocked_balance_in: '0.00',
        blocked_balance_out: '0.00',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBalance(null);
    setFormData({
      merchant_id: '',
      currency_id: '',
      balance: '0.00',
      blocked_balance_in: '0.00',
      blocked_balance_out: '0.00',
    });
  };

  const handleChange = (field: keyof MerchantBalanceFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSelectChange = (field: 'merchant_id' | 'currency_id') => (e: SelectChangeEvent) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedBalance) {
        if (!canUpdate) {
          showNotification('У вас нет прав для редактирования балансов мерчантов', 'error');
          return;
        }
        const { merchant_id: _merchantId, currency_id: _currencyId, ...updateData } = formData;
        await merchantsService.updateMerchantBalance(selectedBalance.id, updateData);
      } else {
        if (!canCreate) {
          showNotification('У вас нет прав для создания балансов мерчантов', 'error');
          return;
        }
        await merchantsService.createMerchantBalance(formData);
      }
      refetch();
      handleCloseDialog();
    } catch (err: any) {
      setFormError(err.message || 'Ошибка при сохранении баланса');
    }
  };

  const handleDelete = async () => {
    if (!selectedBalance) return;
    try {
      if (!canDelete) {
        showNotification('У вас нет прав для удаления балансов мерчантов', 'error');
        return;
      }
      await merchantsService.deleteMerchantBalance(selectedBalance.id);
      refetch();
      setDeleteDialogOpen(false);
      setSelectedBalance(null);
    } catch (err: any) {
      console.error('Error deleting balance:', err);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Балансы мерчантов
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}><Stack direction={{ xs: 'column', md: 'row' }} spacing={2}><TextField fullWidth label="Поиск по мерчанту или ID" value={search} onChange={(event) => setSearch(event.target.value)} /><FormControl sx={{ minWidth: 200 }}><InputLabel>Валюта</InputLabel><Select value={currencyFilter} label="Валюта" onChange={(event) => setCurrencyFilter(event.target.value)}><MenuItem value="">Все</MenuItem>{currencies.map((currency) => <MenuItem key={currency.id} value={currency.id}>{currency.iso_code}</MenuItem>)}</Select></FormControl><Button onClick={() => { setSearch(''); setCurrencyFilter(''); }}>Сбросить</Button></Stack></Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Мерчант</TableCell>
                <TableCell>Валюта</TableCell>
                <TableCell>Баланс</TableCell>
                <TableCell>Заблокировано входящих</TableCell>
                <TableCell>Заблокировано исходящих</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBalances.map((balance) => (
                <TableRow key={balance.id}>
                  <TableCell>{balance.merchant_name}</TableCell>
                  <TableCell>{balance.currency?.iso_code}</TableCell>
                  <TableCell>{balance.balance}</TableCell>
                  <TableCell>{balance.blocked_balance_in}</TableCell>
                  <TableCell>{balance.blocked_balance_out}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={canUpdate ? 'Редактировать' : 'Недостаточно прав'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (!canUpdate) {
                                showNotification('У вас нет прав для редактирования балансов мерчантов', 'error');
                                return;
                              }
                              handleOpenDialog(balance);
                            }}
                            disabled={!canUpdate}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={canDelete ? 'Удалить' : 'Недостаточно прав'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (!canDelete) {
                                showNotification('У вас нет прав для удаления балансов мерчантов', 'error');
                                return;
                              }
                              setSelectedBalance(balance);
                              setDeleteDialogOpen(true);
                            }}
                            color="error"
                            disabled={!canDelete}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedBalance ? 'Редактировать баланс' : 'Добавить баланс'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal" disabled={Boolean(selectedBalance)}>
              <InputLabel>Мерчант</InputLabel>
              <Select
                value={formData.merchant_id}
                onChange={handleSelectChange('merchant_id')}
                label="Мерчант"
                required
              >
                {merchants.map((merchant) => (
                  <MenuItem key={merchant.id} value={merchant.id}>
                    {merchant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" disabled={Boolean(selectedBalance)}>
              <InputLabel>Валюта</InputLabel>
              <Select
                value={formData.currency_id}
                onChange={handleSelectChange('currency_id')}
                label="Валюта"
                required
              >
                {currencies.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.iso_code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Баланс"
              type="number"
              value={formData.balance}
              onChange={handleChange('balance')}
              margin="normal"
              required
              inputProps={{ step: "0.01" }}
            />

            <TextField
              fullWidth
              label="Заблокировано (входящие)"
              type="number"
              value={formData.blocked_balance_in}
              onChange={handleChange('blocked_balance_in')}
              margin="normal"
              required
              inputProps={{ step: "0.01" }}
            />

            <TextField
              fullWidth
              label="Заблокировано (исходящие)"
              type="number"
              value={formData.blocked_balance_out}
              onChange={handleChange('blocked_balance_out')}
              margin="normal"
              required
              inputProps={{ step: "0.01" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedBalance ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedBalance(null);
        }}
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить баланс для мерчанта "{selectedBalance?.merchant_name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedBalance(null);
            }}
          >
            Отмена
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default MerchantBalances;
